import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesList = () => {
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchServices();
    if (['admin', 'manager'].includes(user?.role)) {
      fetchStaff();
    }
  }, [user]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to fetch services');
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`);
      setStaff(response.data.filter(s => s.role === 'technician'));
    } catch (error) {
      console.error('Failed to fetch staff for assignment');
    }
  };

  const handleUpdateStatus = async (serviceId, newStatus) => {
    try {
      await axios.put(`${API}/services/${serviceId}/status`, {
        status: newStatus
      });
      toast.success('Service status updated');
      fetchServices();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssignTechnician = async (serviceId, technicianId) => {
    try {
      await axios.put(`${API}/services/${serviceId}/assign`, {
        technician_id: technicianId
      });
      toast.success('Technician assigned successfully');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service request?')) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      assigned: 'bg-blue-500',
      in_progress: 'bg-purple-500',
      completed: 'bg-green-600',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-slate-500';
  };

  return (
    <div data-testid="services-list-page">
      <h2 className="text-2xl font-heading font-bold uppercase mb-6">Service Requests</h2>
      <div className="space-y-4">
        {services.length === 0 ? (
          <Card className="p-8 text-center border-slate-200">
            <p className="text-slate-600">No service requests found</p>
          </Card>
        ) : (
          services.map(service => (
            <Card key={service.id} className="p-6 border-slate-200" data-testid={`service-${service.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{service.id}</h3>
                  <p className="text-sm text-slate-600">{service.user_name} • {service.user_email}</p>
                  <p className="text-sm font-semibold text-primary mt-1">{service.service_type}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge className={`${getStatusColor(service.status)} text-white`} data-testid={`service-status-${service.id}`}>
                    {service.status.replace('_', ' ')}
                  </Badge>
                  {['admin', 'manager'].includes(user?.role) && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteService(service.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p><strong>Address:</strong> {service.address}</p>
                  <p><strong>Mobile:</strong> <a href={`tel:${service.mobile}`} className="text-primary hover:underline">{service.mobile}</a></p>
                  <p><strong>Desired Date:</strong> {service.preferred_date} at {service.preferred_time}</p>
                </div>
                <div className="space-y-2">
                  <p><strong>Issue:</strong> {service.issue_description}</p>
                  {service.technician_name && (
                    <p className="text-blue-600 font-medium"><strong>Assigned to:</strong> {service.technician_name}</p>
                  )}
                </div>
              </div>

              {/* Technician View Controls */}
              {user?.role === 'technician' && service.technician_id === user.id && (
                <div className="mt-4 pt-4 border-t flex gap-2">
                  {service.status === 'assigned' && (
                    <Button
                      onClick={() => handleUpdateStatus(service.id, 'in_progress')}
                      className="bg-primary hover:bg-primary/90"
                      data-testid={`start-service-${service.id}`}
                    >
                      Start Service
                    </Button>
                  )}
                  {service.status === 'in_progress' && (
                    <Button
                      onClick={() => handleUpdateStatus(service.id, 'completed')}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`complete-service-${service.id}`}
                    >
                      Mark Complete
                    </Button>
                  )}
                </div>
              )}

              {/* Admin/Manager Assignment Controls */}
              {['admin', 'manager'].includes(user?.role) && (
                <div className="mt-6 pt-4 border-t space-y-4">
                  <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase font-bold text-slate-500">Tech Assignment</p>
                    <div className="flex gap-2">
                      <select 
                        className="text-sm border border-slate-200 rounded-md p-2 flex-1"
                        value={service.technician_id || ''}
                        onChange={(e) => handleAssignTechnician(service.id, e.target.value)}
                        data-testid={`assign-tech-${service.id}`}
                      >
                        <option value="">Select Technician</option>
                        {staff.map(member => (
                          <option key={member.id} value={member.id}>{member.name}</option>
                        ))}
                      </select>
                      <Button variant="outline" size="sm" onClick={() => fetchServices()}>Refresh</Button>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase font-bold text-slate-500">Force Status Update</p>
                    <div className="flex gap-2">
                      <select 
                        className="text-sm border border-slate-200 rounded-md p-2 flex-1"
                        value={service.status}
                        onChange={(e) => handleUpdateStatus(service.id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="assigned">Assigned</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Staff View Control (Status only, no assignment) */}
              {user?.role === 'staff' && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs uppercase font-bold text-slate-500 mb-2">Update Status</p>
                  <select 
                    className="text-sm border border-slate-200 rounded-md p-2 w-full"
                    value={service.status}
                    onChange={(e) => handleUpdateStatus(service.id, e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ServicesList;
